import { GuarantorDto } from "api/tenant"
import { Button } from "components/ui/Button"
import { LightAction } from "components/ui/LightAction"
import { ManIcon } from "components/icons/customIcons"
import { AddIcon, EditIcon, TrashIcon, UserIcon } from "components/icons/Icons"
import { Sizes } from "components/forms/NumberSelector"
import { BaseSection } from "components/layout/Section/BaseSection"
import { NsKey } from "enums/NsKey"
import { useFormikContext } from "formik"
import { useDialog } from "hooks/useDialog"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { useCallback, useMemo } from "react"
import { TenantFormFieldNames } from "../../types/tenantFormFieldNames"
import { TenantFormValues } from "../../types/tenantFormValues"
import { TenantGuarantorForm } from "./TenantGuarantorForm"

export const TenantGuarantorSection = () => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const { values, setFieldValue } = useFormikContext<TenantFormValues>()

  const { openDialog } = useDialog()

  const addGuarantor = useCallback((guarantor: GuarantorDto) => {
    setFieldValue(TenantFormFieldNames.Guarantor, guarantor)
  }, [setFieldValue])

  const editGuarantor = useCallback((guarantor: GuarantorDto) => {
    setFieldValue(TenantFormFieldNames.Guarantor, guarantor)
  }, [setFieldValue])

  const deleteGuarantor = useCallback(() => {
    setFieldValue(TenantFormFieldNames.Guarantor, undefined)
  }, [setFieldValue])

  const openGuarantorAddForm = useCallback(() => {
    openDialog({
      title: "Ajouter un garant",
      size: Sizes["4xl"]
    },
      <TenantGuarantorForm onSubmit={addGuarantor} />)
  }, [openDialog, addGuarantor])

  const openGuarantorEditForm = useCallback((guarantor: GuarantorDto) => {
    openDialog({
      title: "Modifier un garant",
      size: Sizes["4xl"]
    },
      <TenantGuarantorForm onSubmit={editGuarantor} guarantor={guarantor} />)
  }, [openDialog, editGuarantor])

  const guarantors = useMemo(() => {
    if (!values[TenantFormFieldNames.Guarantor]) {
      return []
    }
    return [values[TenantFormFieldNames.Guarantor]]
  }, [values])

  const renderGuarantor = useCallback((guarantor: GuarantorDto, index: number) => {
    return <div
      key={`${guarantor.id}-${index}`}
      className="border shadow text-sm rounded-lg bg-white px-4 py-2 flex items-center"
    >
      <div className="grid grid-cols-12 gap-4 divide-x w-full">
        <div className="col-span-4 flex items-center gap-4">
          <ManIcon />
          {`${guarantor.lastName} ${guarantor.firstName}`}
        </div>
        <div className="col-span-8 px-4 flex items-center">
          <div className="text-primary">
            {guarantor.email}
          </div>
        </div>
      </div>
      <div className="flex gap-2 items-center justify-end">
        <Button
          round
          color='slate'
          onClick={() => openGuarantorEditForm(guarantor)}
        >
          <EditIcon className="w-5 h-5" />
        </Button>
        <Button
          round
          color='slate'
          onClick={deleteGuarantor}
        >
          <TrashIcon className="w-5 h-5" />
        </Button>
      </div>
    </div>
  }, [openGuarantorEditForm, deleteGuarantor])

  if (guarantors.length === 0) {
    return <BaseSection title={t('TENANT.SECTIONS.GUARANTOR_SECTION')}>
      <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border bg-white">
        <UserIcon className="h-16 w-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucun garant renseigne
        </h3>
        <p className="text-gray-500 mb-6 max-w-md">
          Veuillez renseigner un garant
        </p>
        <LightAction
          icon={AddIcon}
          label={"Ajouter un garant"}
          onClick={openGuarantorAddForm}
        />
      </div>
    </BaseSection>
  }

  return <BaseSection title={t('TENANT.SECTIONS.GUARANTOR_SECTION')}>
    <div className="p-4 rounded-xl border shadow bg-white">
      {guarantors &&
        <div className="space-y-2 mb-4">
          {guarantors.map(renderGuarantor)}
        </div>
      }
      <button
        className="w-full flex justify-center bg-white hover:bg-primary/5 text-primary gap-4 border-2 border-primary/20 hover:border-primary/50 rounded-md border-dashed p-2"
        onClick={(e) => {
          e.preventDefault()
          openGuarantorAddForm()
        }}
      >
        <LightAction
          icon={AddIcon}
          label={"Ajouter un garant"}
        />
      </button>
    </div>
  </BaseSection>
}
